import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/PresenzaForm.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function PresenzaForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [allenamenti, setAllenamenti] = useState([]);
  const [allenamentoSelezionatoId, setAllenamentoSelezionatoId] = useState("");
  const [presenze, setPresenze] = useState([]);

  const [loadingAllenamenti, setLoadingAllenamenti] = useState(false);
  const [loadingPresenze, setLoadingPresenze] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadAllenamenti = async () => {
      setLoadingAllenamenti(true);

      try {
        const res = await authFetch(`${API_URL}/api/allenamenti/miei/futuri`);

        if (!res.ok) {
          throw new Error("Errore nel caricamento degli allenamenti");
        }

        const data = await res.json().catch(() => []);

        if (mounted) {
          setAllenamenti(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error(error);
        toast.error("Impossibile caricare gli allenamenti.");
        if (mounted) setAllenamenti([]);
      } finally {
        if (mounted) setLoadingAllenamenti(false);
      }
    };

    loadAllenamenti();

    return () => {
      mounted = false;
    };
  }, [authFetch]);

  const allenamentoSelezionato = useMemo(() => {
    if (!allenamentoSelezionatoId) return null;
    return (
      allenamenti.find((a) => String(a.id) === String(allenamentoSelezionatoId)) ||
      null
    );
  }, [allenamenti, allenamentoSelezionatoId]);

  useEffect(() => {
    let mounted = true;

    const loadPresenze = async () => {
      if (!allenamentoSelezionatoId) {
        setPresenze([]);
        return;
      }

      setLoadingPresenze(true);

      try {
        const res = await authFetch(
          `${API_URL}/api/admin/presenze/genera/${allenamentoSelezionatoId}`,
          { method: "POST" }
        );

        if (!res.ok) {
          throw new Error("Errore nel caricamento delle presenze");
        }

        const data = await res.json().catch(() => []);

        if (mounted) {
          setPresenze(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error(error);
        toast.error("Impossibile caricare le presenze.");
        if (mounted) setPresenze([]);
      } finally {
        if (mounted) setLoadingPresenze(false);
      }
    };

    loadPresenze();

    return () => {
      mounted = false;
    };
  }, [allenamentoSelezionatoId, authFetch]);

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(`${value}T00:00:00`);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("it-IT");
  };

  const formatTime = (value) => {
    if (!value) return "—";
    return value?.slice?.(0, 5) || value;
  };

  const handleTogglePresenza = (idGiocatore) => {
    setPresenze((prev) =>
      prev.map((p) =>
        String(p.idGiocatore) === String(idGiocatore)
          ? { ...p, presente: !p.presente }
          : p
      )
    );
  };

  const handleSelectAll = (value) => {
    setPresenze((prev) => prev.map((p) => ({ ...p, presente: value })));
  };

  const presentiCount = presenze.filter((p) => !!p.presente).length;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!allenamentoSelezionatoId) {
      toast.error("Seleziona un allenamento.");
      return;
    }

    if (presenze.length === 0) {
      toast.error("Nessuna presenza da salvare.");
      return;
    }

    const payload = {
      idAllenamento: Number(allenamentoSelezionatoId),
      items: presenze.map((p) => ({
        idGiocatore: p.idGiocatore,
        presente: !!p.presente
      }))
    };

    setSaving(true);

    try {
      const res = await authFetch(`${API_URL}/api/admin/presenze/batch`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error("Errore nel salvataggio delle presenze");
      }

      const data = await res.json().catch(() => []);

      if (Array.isArray(data)) {
        setPresenze(data);
      }

      toast.success("Presenze salvate con successo.");
    } catch (error) {
      console.error(error);
      toast.error("Errore durante il salvataggio delle presenze.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="presenze-page" translate="no">
      <div className="presenze-shell">
        <div className="presenze-header-card">
          <div>
            <span className="presenze-badge">Registro presenze</span>
            <h2>Presenze Allenamento</h2>
            <p>
              Seleziona un allenamento associato allo staff loggato e registra
              le presenze dei giocatori.
            </p>
          </div>
        </div>

        <form className="presenze-form-card" onSubmit={handleSubmit}>
          <div className="presenze-top-grid">
            <div className="presenze-field">
              <label>Allenamento</label>
              <select
                value={allenamentoSelezionatoId}
                onChange={(e) => setAllenamentoSelezionatoId(e.target.value)}
                disabled={loadingAllenamenti || saving}
              >
                <option value="">
                  {loadingAllenamenti
                    ? "Caricamento allenamenti..."
                    : "Seleziona allenamento"}
                </option>

                {allenamenti.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nomeCategoria} - {formatDate(a.dataAllenamento)} -{" "}
                    {formatTime(a.oraInizio)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {allenamentoSelezionato && (
            <div className="presenze-context-card">
              <h3>Dettaglio allenamento</h3>

              <div className="presenze-context-grid">
                <div className="presenze-context-item">
                  <span className="presenze-context-label">Categoria</span>
                  <strong>{allenamentoSelezionato.nomeCategoria || "—"}</strong>
                </div>

                <div className="presenze-context-item">
                  <span className="presenze-context-label">Data</span>
                  <strong>
                    {formatDate(allenamentoSelezionato.dataAllenamento)}
                  </strong>
                </div>

                <div className="presenze-context-item">
                  <span className="presenze-context-label">Ora inizio</span>
                  <strong>{formatTime(allenamentoSelezionato.oraInizio)}</strong>
                </div>

                <div className="presenze-context-item">
                  <span className="presenze-context-label">Ora fine</span>
                  <strong>{formatTime(allenamentoSelezionato.oraFine)}</strong>
                </div>

                <div className="presenze-context-item">
                  <span className="presenze-context-label">Campo</span>
                  <strong>{allenamentoSelezionato.campo || "Non indicato"}</strong>
                </div>

                <div className="presenze-context-item">
                  <span className="presenze-context-label">Allenatore</span>
                  <strong>
                    {allenamentoSelezionato.nomeAllenatoreResponsabile || "—"}
                  </strong>
                </div>
              </div>
            </div>
          )}

          {allenamentoSelezionato && (
            <div className="presenze-actions-card">
              <div className="presenze-actions-left">
                <h3>Presenze giocatori</h3>
                <p>
                  Presenti selezionati: <strong>{presentiCount}</strong> /{" "}
                  {presenze.length}
                </p>
              </div>

              <div className="presenze-actions-right">
                <button
                  type="button"
                  className="presenze-btn secondary"
                  onClick={() => handleSelectAll(true)}
                  disabled={loadingPresenze || saving || presenze.length === 0}
                >
                  Seleziona tutti
                </button>

                <button
                  type="button"
                  className="presenze-btn secondary"
                  onClick={() => handleSelectAll(false)}
                  disabled={loadingPresenze || saving || presenze.length === 0}
                >
                  Deseleziona tutti
                </button>
              </div>
            </div>
          )}

          {allenamentoSelezionato && (
            <div className="presenze-players-card">
              {loadingPresenze ? (
                <div className="presenze-empty-state">
                  Caricamento presenze...
                </div>
              ) : presenze.length === 0 ? (
                <div className="presenze-empty-state">
                  Nessuna presenza disponibile per l'allenamento selezionato.
                </div>
              ) : (
                <div className="presenze-players-grid">
                  {presenze.map((p) => {
                    const checked = !!p.presente;

                    return (
                      <div
                        key={p.id}
                        className={`presenza-player-card ${
                          checked ? "is-present" : ""
                        }`}
                      >
                        <div className="presenza-player-main">
                          <div className="presenza-player-name">
                            {p.cognomeGiocatore} {p.nomeGiocatore}
                          </div>

                        </div>

                        <label className="presenza-toggle">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleTogglePresenza(p.idGiocatore)}
                            disabled={saving}
                          />
                          <span>{checked ? "Presente" : "Assente"}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="presenze-footer-buttons">
            <button
              type="submit"
              className="presenze-btn primary"
              disabled={!allenamentoSelezionato || saving || loadingPresenze}
            >
              {saving ? "Salvataggio..." : "Salva Presenze"}
            </button>

            <button
              type="button"
              className="presenze-btn ghost"
              onClick={() => navigate("/gestionale")}
              disabled={saving}
            >
              Torna al Gestionale
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}